import React from "react";
import {
    alpha,
    Box,
    Collapse,
    Divider,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import {IconButtonWithLabel} from "../../general/button/IconButtonWithLabel";
import {CustomMultiSelect, StyledOption} from "../../general/inputs/CustomMultiSelect";
import {makeRequest, subState} from "../../../services/requestService";
import {API_URL} from "../../../constants";
import {useHistory} from "react-router-dom";
import {grey} from "@mui/material/colors";
import {useTranslation} from "react-i18next";
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import {useRecoilValue} from "recoil";
import Annotator from "./Annotator/Annotator";
import ChipsTab from "./chipsTabs/ChipsTab";
import ChipsTabSkeleton from "./chipsTabs/ChipsTabSkeleton";
import {findUsedLabelsAndCategories} from "./utils";


export default function LabelViewer({text, onAnnotationChange}) {
    const theme = useTheme();
    const history = useHistory();
    const {t} = useTranslation("common");
    const sub = useRecoilValue(subState);
    const xlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const [loading, setLoading] = React.useState(true);
    const [usedLabels, setUsedLabels] = React.useState([]);
    const [usedCategories, setUsedCategories] = React.useState([]);
    const [selectedCategories, setSelectedCategories] = React.useState([]);
    // Label tabs
    const [selectedLabels, setSelectedLabels] = React.useState([]);
    const [expanded, setExpanded] = React.useState(false);
    const [labelSelectMode, setLabelSelectMode] = React.useState('and');
    const [openAnnotator, setOpenAnnotator] = React.useState(false);

    const load = () => {
        setLoading(true);
        const labelsAndCategories = findUsedLabelsAndCategories(text);
        if (labelsAndCategories.categoriesID.size > 0) {
            let labels = new Set();
            let validID = [];
            labelsAndCategories.categoriesID.forEach((categoryID) => {
                if (Number.isInteger(categoryID))
                    validID.push(categoryID);
            });
            if (validID.length > 0)
                makeRequest(`${API_URL}/label/sets?requestedSet=${validID.join("&requestedSet=")}`).then((response) => {
                    if (response.status === 200 && Array.isArray(response.data)) {
                        setUsedCategories(response.data);
                        // set used labels
                        response.data.forEach((category) => {
                            category.labels.forEach((label) => {
                                let isInText = labelsAndCategories.labelsID.has(label.id);
                                let isAlreadyAdded = labels.has(label);
                                if (isInText && !isAlreadyAdded)
                                    labels.add(label);
                            });
                        });
                        setUsedLabels([...labels]);
                        setLoading(false);
                    } else if (response.status === 400 || response.status > 403) {
                        if (response.status === 404)
                            history.replace("/error/notfound");
                        else
                            history.push("/error");
                    }
                });
        } else {
            setUsedLabels([]);
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (text) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, sub]);

    const handleChangeSelectedLabels = (newValue) => {
        setSelectedLabels(prevSate => {
            let item = prevSate.indexOf(newValue);
            if (item !== -1) {
                prevSate.splice(item, 1);
            } else {
                prevSate.push(newValue);
            }
            return [...prevSate];
        });
    }

    const handleAnnotatorClosed = () => {
        setOpenAnnotator(false);
        onAnnotationChange();
    }


    const handleChangeMode = (event, newValue) => {
        if (newValue !== null)
            setLabelSelectMode(newValue);
    }

    const handleChangeSelectedCategories = (newValues) => {
        let newSelectedLabels = [];
        if (selectedCategories.length > 0) {
            newSelectedLabels = [...selectedLabels];
            let removedCategory = selectedCategories.filter(el => !newValues.includes(el));
            removedCategory.forEach((catID) => {
                let category = usedCategories.find(c => c.id === catID);
                if (category)
                    newSelectedLabels = newSelectedLabels.filter(el => !category.labels.find(l => l.id === el));
            });
        }
        setSelectedLabels(newSelectedLabels);
        setSelectedCategories(newValues);
    }

    const visibility = (labels) => {
        if (labels.length < 1)
            return false;
        if (labelSelectMode === "eq")
            return labels.length === selectedLabels.length && labels.every(l => selectedLabels.indexOf(l.id) > -1);
        if (labelSelectMode === "and")
            return labels.length >= selectedLabels.length && selectedLabels.every(sl => labels.find(l => l.id === sl));
        return labels.some(l => selectedLabels.indexOf(l.id) > -1);
    }

    // render function for multiselect
    function renderValue(options) {
        if (options.length <= 0)
            return <Typography display="inline" variant="caption" color="textSecondary">
                {t("main.components.labelViewer.menu.select")}
            </Typography>;

        return <>
            {options.map(option => <Typography key={`option-${option.value}`} display="inline" variant="caption">
                {option.label}
            </Typography>)}
        </>;
    }

    // RENDER

    const toolbar = (<>
        {(usedLabels.length > 0 || loading) && <>
            <Grid container
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                      borderBottom: `solid 2px ${theme.palette.primary.dark}`,
                      borderTop: `solid 2px ${theme.palette.primary.dark}`
                  }}
            >
                <Grid item xs={12} md={12} xl={8}>
                    {!loading && <>
                        {selectedCategories.length <= 0 &&
                            <ChipsTab
                                labels={usedLabels}
                                selected={selectedLabels}
                                onChange={handleChangeSelectedLabels}
                                maxWidth={"100%"}
                            />
                        }
                        {selectedCategories.length > 0 && selectedCategories.map((categoryID) => {
                            // Show labels by category
                            // get the category
                            const category = usedCategories.find(c => c.id === categoryID);
                            // show only used labels
                            const labels = category.labels.filter(cl => usedLabels.find(ul => ul.id === cl.id));
                            return <div key={`labeled-chip-tabs-${categoryID}`}>
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="textSecondary"
                                    sx={{
                                        borderBottom: `solid 1px ${grey[300]}`,
                                        maxWidth: "fit-content",
                                        pl: 1,
                                        pr: 2,
                                    }}
                                >{`\u2022\u00A0${category.name}`}</Typography>
                                <ChipsTab
                                    labels={labels}
                                    selected={selectedLabels}
                                    onChange={handleChangeSelectedLabels}
                                    maxWidth={"100%"}
                                />
                            </div>
                        })}
                    </>}
                    {loading && <ChipsTabSkeleton/>}
                </Grid>
                <Grid item xs md lg xl>
                    <Stack direction="row-reverse" spacing={1} m={1} maxWidth="fit-content">
                        {sub !== "readonly" && <Box display="flex" alignItems="center">
                            <IconButtonWithLabel
                                label={t("main.components.labelViewer.menu.annotate")}
                                disabled={loading}
                                onClick={() => setOpenAnnotator(true)}
                            >
                                <BookmarkAddIcon fontSize="small"/>
                            </IconButtonWithLabel>
                        </Box>}
                        <Box display="flex" alignItems="center">
                            <IconButtonWithLabel label={t("main.components.labelViewer.menu.filter")} disabled={loading}
                                                 onClick={() => setExpanded(prevState => !prevState)}>
                                <FilterListIcon fontSize="small"/>
                            </IconButtonWithLabel>
                        </Box>
                        {xlUp && <Divider orientation="vertical" flexItem/>}
                    </Stack>
                </Grid>
            </Grid>
            <Collapse in={expanded}>
                <Toolbar
                    sx={{
                        bgcolor: theme.palette.background.menu,
                        borderBottom: `solid 2px ${theme.palette.primary.dark}`,
                        p: 1
                    }}>
                    <Stack direction="row-reverse" spacing={2} alignItems="center" width="100%">
                        <Stack spacing={1}>
                            <ToggleButtonGroup size="small" exclusive value={labelSelectMode}
                                               onChange={handleChangeMode}>
                                <ToggleButton value="eq">
                                    <Tooltip title={t("main.components.labelViewer.menu.eq")}>
                                        <Typography color="textSecondary"
                                                    variant="caption">==</Typography></Tooltip>
                                </ToggleButton>
                                <ToggleButton value="and">
                                    <Tooltip title={t("main.components.labelViewer.menu.and")}>
                                        <Typography color="textSecondary"
                                                    variant="caption">&&</Typography></Tooltip>
                                </ToggleButton>
                                <ToggleButton value="or">
                                    <Tooltip title={t("main.components.labelViewer.menu.or")}>
                                        <Typography color="textSecondary" variant="caption">|&nbsp;|</Typography>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Typography variant="caption" textAlign="center" width="100%">Mode</Typography>
                        </Stack>
                        <Stack>
                            <CustomMultiSelect
                                value={selectedCategories}
                                onChange={handleChangeSelectedCategories}
                                renderValue={renderValue}
                            >
                                {usedCategories.map((category) => (
                                    <StyledOption key={`category-option-${category.id}`}
                                                  value={category.id}>{category.name}</StyledOption>
                                ))}
                            </CustomMultiSelect>
                            <Typography variant="caption" textAlign="center"
                                        width="100%">{t("main.components.labelViewer.menu.categories")}</Typography>
                        </Stack>
                    </Stack>
                </Toolbar>
            </Collapse>
        </>}
    </>);

    return (
        <Box sx={{cursor: "default"}}>
            {toolbar}
            <Box display="flex" justifyContent="space-around" pl={4} pr={4} minHeight="45px">
                {usedLabels.length <= 0 && sub !== "readonly" &&
                    <Box display="flex" alignItems="center" position="absolute" right={0} top={0}>
                        <Tooltip title={t("main.components.labelViewer.menu.annotate")}>
                            <span>
                                <IconButton
                                    disabled={loading}
                                    onClick={() => setOpenAnnotator(true)}
                                >
                                    <BookmarkAddIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>}
                <Stack justifyContent="center">
                    {text && Array.isArray(text.lines) && text.lines.map((line) => (
                        <Box key={`label-view-line-${line.id}`}>
                            {line && Array.isArray(line.tokens) && line.tokens.map((token) => {
                                const visible = visibility(token.labels);
                                return (
                                    <Stack
                                        key={`label-view-token-${token.id}`}
                                        display="inline-flex"
                                        sx={{
                                            borderRadius: "0.2em",
                                            "&:hover": {
                                                backgroundColor: theme => alpha(theme.palette.secondary.main, 0.2)
                                            }
                                        }}>
                                        <Typography>
                                            {token.type !== "PUNCTUATION" && "\u00A0"}{token.textContent}
                                        </Typography>
                                        <Stack direction="row">
                                            {visible && Array.isArray(token.labels) && token.labels.map((label) => {
                                                if (selectedLabels.find(l => l === label.id)) {
                                                    const labelFromSet = usedLabels.find(ul => ul.id === label.id);
                                                    return (
                                                        <span
                                                            key={`label-view-token-${token.id}-${label.id}`}
                                                            style={{
                                                                backgroundColor: labelFromSet.color,
                                                                height: "4px",
                                                                width: "100%"
                                                            }}/>
                                                    );
                                                }
                                                return null;
                                            })}
                                            {!visible && <span
                                                key={`label-view-token-${token.id}-hidden`}
                                                style={{
                                                    border: `solid 2px ${alpha("#FFF", 0.0)}`,
                                                    width: "100%"
                                                }}/>}
                                        </Stack>
                                    </Stack>
                                );
                            })}
                        </Box>
                    ))}
                </Stack>
            </Box>
            <Annotator
                open={openAnnotator}
                onClose={handleAnnotatorClosed}
                textID={text.id}
            />
        </Box>
    );
}